import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import { useApprovalWS } from '../context/ApprovalWSContext';
import ApprovalDetailPanel from '../components/ApprovalDetailPanel';
import {
  CheckCircle,
  CircleNotch,
  Clock,
  Funnel,
  Lightning,
  MagnifyingGlass,
  Prohibit,
  Shield,
  SortAscending,
  Warning,
  WarningCircle,
  X,
} from '@phosphor-icons/react';

const RISK_STYLES = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'CRITICAL' },
  high: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', label: 'HIGH' },
  medium: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'MEDIUM' },
  low: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', label: 'LOW' },
};

const TYPE_STYLES = {
  execution: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Execution' },
  modification: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Modification' },
  publish: { bg: 'bg-teal-500/15', text: 'text-teal-400', label: 'Publish' },
  escalation: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'Escalation' },
  configuration: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', label: 'Configuration' },
};

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Pending' },
  approved: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', label: 'Approved' },
  denied: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Denied' },
  expired: { bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Expired' },
};

const TABS = [
  { id: 'pending', label: 'Pending Approvals' },
  { id: 'decisions', label: 'Recent Decisions' },
  { id: 'outcomes', label: 'Execution Outcomes' },
];

export default function ApprovalDashboard() {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [activeTab, setActiveTab] = useState('pending');
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [executionOutcomes, setExecutionOutcomes] = useState([]);
  const [loadingOutcomes, setLoadingOutcomes] = useState(false);
  const [intelligence, setIntelligence] = useState(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchApprovals = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      let statusParam = '';
      if (activeTab === 'pending') statusParam = '&status=pending';
      else if (activeTab === 'decisions') statusParam = '&status=approved&status=denied';

      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/approvals?limit=50${statusParam}`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setApprovals(data.approvals || []);
      }
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, activeTab]);

  const fetchPendingCount = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/approvals/pending/count`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  }, [orgId]);

  const fetchExecutionOutcomes = useCallback(async () => {
    if (!orgId) return;
    setLoadingOutcomes(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/decision-intelligence/outcomes?limit=20`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setExecutionOutcomes(data.outcomes || []);
      }
    } catch (err) {
      console.error('Failed to fetch outcomes:', err);
    } finally {
      setLoadingOutcomes(false);
    }
  }, [orgId]);

  const fetchIntelligence = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/decision-intelligence/summary`,
        {},
        orgId
      );
      if (res.ok) {
        setIntelligence(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch intelligence:', err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchApprovals();
    fetchPendingCount();
    fetchIntelligence();
  }, [fetchApprovals, fetchPendingCount, fetchIntelligence]);

  useEffect(() => {
    if (activeTab === 'outcomes') {
      fetchExecutionOutcomes();
    }
  }, [activeTab, fetchExecutionOutcomes]);

  const handleResolved = () => {
    fetchApprovals();
    fetchPendingCount();
    fetchIntelligence();
    setSelectedApproval(null);
  };

  // WebSocket: Live dashboard updates
  const handleWSApprovalCreated = useCallback((event) => {
    fetchPendingCount();
    fetchIntelligence();
    if (activeTab === 'pending') {
      fetchApprovals();
    }
  }, [activeTab, fetchPendingCount, fetchIntelligence, fetchApprovals]);

  const handleWSApprovalResolved = useCallback((event) => {
    fetchPendingCount();
    fetchIntelligence();
    // Update the approval in-place if it's in the current list
    setApprovals((prev) =>
      prev.map((a) =>
        a.approval_id === event.approval_id
          ? { ...a, status: event.status, resolved_at: event.resolved_at, resolved_by: event.resolved_by }
          : a
      )
    );
    // Update the selected detail drawer if it's showing this approval
    if (selectedApproval?.approval_id === event.approval_id) {
      setSelectedApproval((prev) =>
        prev ? { ...prev, status: event.status, resolved_at: event.resolved_at, resolved_by: event.resolved_by } : prev
      );
    }
    // Refresh data for the current tab
    if (activeTab === 'decisions' || activeTab === 'pending') {
      fetchApprovals();
    }
    if (activeTab === 'outcomes') {
      fetchExecutionOutcomes();
    }
  }, [activeTab, selectedApproval, fetchPendingCount, fetchIntelligence, fetchApprovals, fetchExecutionOutcomes]);

  useApprovalWS('approval_created', handleWSApprovalCreated);
  useApprovalWS('approval_resolved', handleWSApprovalResolved);

  // Filter & sort
  const filteredApprovals = approvals
    .filter((a) => {
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      const risk = a.context?.capability_risk_level || a.trigger_details?.risk_level || '';
      if (riskFilter !== 'all' && risk !== riskFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = [a.reason, a.target_type, a.target_id, a.type, a.trigger_type].join(' ').toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'risk') {
        const riskOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        const ra = riskOrder[a.context?.capability_risk_level || 'low'] ?? 3;
        const rb = riskOrder[b.context?.capability_risk_level || 'low'] ?? 3;
        return ra - rb;
      }
      return 0;
    });

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6" data-testid="approval-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="approval-dashboard-title">
            Approval Dashboard
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            AI OS Control Room — Review, approve, and monitor governed system actions
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <Warning size={18} className="text-yellow-400" />
            <span className="text-sm text-yellow-400 font-medium" data-testid="pending-count-header">
              {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Intelligence Summary */}
      {intelligence && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="intelligence-summary">
          <StatCard
            label="Approval Rate"
            value={`${(intelligence.approvals?.approval_rate * 100 || 0).toFixed(0)}%`}
            sub={`${intelligence.approvals?.approved || 0} of ${(intelligence.approvals?.approved || 0) + (intelligence.approvals?.denied || 0)}`}
            color="text-green-400"
          />
          <StatCard
            label="Optimization Success"
            value={`${(intelligence.optimization?.success_rate * 100 || 0).toFixed(0)}%`}
            sub={`${intelligence.optimization?.total_successes || 0} of ${intelligence.optimization?.total_executions || 0} executions`}
            color="text-blue-400"
          />
          <StatCard
            label="Avg Improvement"
            value={intelligence.optimization?.avg_improvement_score
              ? `${(intelligence.optimization.avg_improvement_score > 0 ? '+' : '')}${(intelligence.optimization.avg_improvement_score * 100).toFixed(0)}%`
              : '—'}
            sub="score across outcomes"
            color="text-[#6366F1]"
          />
          <StatCard
            label="Pipeline Success"
            value={`${(intelligence.execution_pipeline?.success_rate * 100 || 0).toFixed(0)}%`}
            sub={`${intelligence.execution_pipeline?.total || 0} total executions`}
            color="text-cyan-400"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[rgba(255,255,255,0.08)]" data-testid="approval-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative px-5 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <span className="flex items-center gap-2">
              {tab.label}
              {tab.id === 'pending' && pendingCount > 0 && (
                <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[11px] font-bold bg-yellow-500 text-black rounded-full">
                  {pendingCount}
                </span>
              )}
            </span>
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#6366F1]" />
            )}
          </button>
        ))}
      </div>

      {/* Filters — shown for pending and decisions tabs */}
      {activeTab !== 'outcomes' && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="approval-filters">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search approvals..."
              className="w-full pl-9 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
              data-testid="search-approvals"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#6366F1] appearance-none cursor-pointer"
            data-testid="filter-type"
          >
            <option value="all">All Types</option>
            <option value="execution">Execution</option>
            <option value="modification">Modification</option>
            <option value="publish">Publish</option>
            <option value="escalation">Escalation</option>
            <option value="configuration">Configuration</option>
          </select>
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#6366F1] appearance-none cursor-pointer"
            data-testid="filter-risk"
          >
            <option value="all">All Risk</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#6366F1] appearance-none cursor-pointer"
            data-testid="sort-by"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="risk">Highest Risk</option>
          </select>
          {(searchQuery || typeFilter !== 'all' || riskFilter !== 'all') && (
            <button
              onClick={() => { setSearchQuery(''); setTypeFilter('all'); setRiskFilter('all'); }}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              data-testid="clear-filters"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={32} className="animate-spin text-[#6366F1]" />
        </div>
      ) : (
        <>
          {activeTab === 'pending' && (
            <ApprovalList
              approvals={filteredApprovals}
              onSelect={setSelectedApproval}
              emptyIcon={<CheckCircle size={48} className="text-green-500/40" />}
              emptyTitle="All clear"
              emptyDesc="No pending approvals. The system is running within governed parameters."
            />
          )}
          {activeTab === 'decisions' && (
            <ApprovalList
              approvals={filteredApprovals}
              onSelect={setSelectedApproval}
              emptyIcon={<Clock size={48} className="text-gray-500/40" />}
              emptyTitle="No decisions yet"
              emptyDesc="Resolved approvals will appear here."
            />
          )}
          {activeTab === 'outcomes' && (
            <ExecutionOutcomes
              outcomes={executionOutcomes}
              loading={loadingOutcomes}
            />
          )}
        </>
      )}

      {/* Detail Drawer */}
      <ApprovalDetailPanel
        approval={selectedApproval}
        isOpen={!!selectedApproval}
        onClose={() => setSelectedApproval(null)}
        onResolved={handleResolved}
        orgId={orgId}
      />
    </div>
  );
}

/* ===== Approval List ===== */
function ApprovalList({ approvals, onSelect, emptyIcon, emptyTitle, emptyDesc }) {
  if (!approvals.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
        {emptyIcon}
        <h3 className="text-lg font-medium text-gray-300 mt-4">{emptyTitle}</h3>
        <p className="text-sm text-gray-500 mt-1 max-w-md">{emptyDesc}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="approval-list">
      {approvals.map((approval) => (
        <ApprovalRow key={approval.approval_id} approval={approval} onClick={() => onSelect(approval)} />
      ))}
    </div>
  );
}

/* ===== Single Approval Row ===== */
function ApprovalRow({ approval, onClick }) {
  const decision = approval.context?.decision || {};
  const risk = decision.risk_level || approval.context?.capability_risk_level || approval.trigger_details?.risk_level || null;
  const riskStyle = RISK_STYLES[risk] || null;
  const typeStyle = TYPE_STYLES[approval.type] || TYPE_STYLES.execution;
  const statusStyle = STATUS_STYLES[approval.status] || STATUS_STYLES.pending;
  const isPending = approval.status === 'pending';
  const confidence = decision.factors?.confidence || approval.context?.confidence;
  const explanation = decision.explanation;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition-all ${
        isPending
          ? 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] hover:border-[#6366F1]/40 hover:bg-[rgba(99,102,241,0.05)]'
          : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.05)] hover:border-[rgba(255,255,255,0.12)]'
      }`}
      data-testid={`approval-row-${approval.approval_id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${typeStyle.bg} ${typeStyle.text}`}>
              {typeStyle.label}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
              {statusStyle.label}
            </span>
            {riskStyle && (
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${riskStyle.bg} ${riskStyle.text} ${riskStyle.border}`}>
                {riskStyle.label} RISK
              </span>
            )}
            {confidence != null && (
              <span className="px-2 py-0.5 text-[10px] font-medium bg-[#6366F1]/10 text-[#6366F1] rounded">
                {(confidence * 100).toFixed(0)}% confidence
              </span>
            )}
            <span className="text-[11px] text-gray-600 capitalize">
              {approval.target_type}: {approval.target_id?.slice(0, 20) || 'system'}
            </span>
          </div>
          <p className="text-sm text-gray-200 line-clamp-2">
            {explanation || approval.reason || 'Approval required'}
          </p>
          {approval.trigger_type && !explanation && (
            <p className="text-xs text-gray-500 mt-1">
              Trigger: {approval.trigger_type.replace(/_/g, ' ')}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">{formatTime(approval.created_at)}</p>
          {approval.resolved_by && (
            <p className="text-xs text-gray-600 mt-1">
              by {approval.resolved_by.slice(0, 12)}...
            </p>
          )}
          {isPending && (
            <span className="inline-block mt-2 text-xs text-[#6366F1] font-medium">
              Review →
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* ===== Execution Outcomes ===== */
function ExecutionOutcomes({ outcomes, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={32} className="animate-spin text-[#6366F1]" />
      </div>
    );
  }

  if (!outcomes.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-outcomes">
        <Lightning size={48} className="text-gray-500/40" />
        <h3 className="text-lg font-medium text-gray-300 mt-4">No execution outcomes yet</h3>
        <p className="text-sm text-gray-500 mt-1 max-w-md">
          Execution results from approved actions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="outcomes-list">
      {outcomes.map((outcome, idx) => {
        const isSuccess = outcome.success || outcome.optimization_success;
        const score = outcome.outcome_score ?? outcome.improvement_score;
        const techSuccess = outcome.context?.technical_success ?? outcome.technical_success;
        const optSuccess = outcome.context?.optimization_success ?? outcome.optimization_success;
        return (
          <div
            key={outcome.recommendation_id || outcome.execution_id || idx}
            className="p-4 rounded-lg border bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)]"
            data-testid={`outcome-row-${idx}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {isSuccess ? (
                    <CheckCircle size={16} className="text-green-400" weight="fill" />
                  ) : (
                    <WarningCircle size={16} className="text-red-400" weight="fill" />
                  )}
                  <span className="text-sm text-white font-medium capitalize">
                    {outcome.action_type?.replace(/_/g, ' ') || outcome.recommendation_type?.replace(/_/g, ' ') || 'Execution'}
                  </span>
                  {score != null && (
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                      score > 0 ? 'bg-green-500/15 text-green-400' : score < 0 ? 'bg-red-500/15 text-red-400' : 'bg-gray-500/15 text-gray-400'
                    }`}>
                      {score > 0 ? '+' : ''}{(score * 100).toFixed(0)}%
                    </span>
                  )}
                  {/* Dual success indicators */}
                  <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${
                    techSuccess ? 'bg-green-500/10 text-green-400' : techSuccess === false ? 'bg-red-500/10 text-red-400' : 'bg-gray-500/10 text-gray-500'
                  }`}>
                    TECH {techSuccess ? 'OK' : techSuccess === false ? 'FAIL' : '—'}
                  </span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${
                    optSuccess ? 'bg-blue-500/10 text-blue-400' : optSuccess === false ? 'bg-orange-500/10 text-orange-400' : 'bg-gray-500/10 text-gray-500'
                  }`}>
                    OPT {optSuccess ? 'OK' : optSuccess === false ? 'NO' : '—'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 line-clamp-1">
                  {outcome.target_id ? `Target: ${outcome.target_id}` : ''}
                  {outcome.total_executions ? ` · ${outcome.total_executions} executions` : ''}
                </p>
                {outcome.context?.risk_level && (
                  <span className="text-[10px] text-gray-500 capitalize">Risk: {outcome.context.risk_level}</span>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-gray-500">{formatTime(outcome.last_applied)}</p>
                {outcome.total_successes != null && outcome.total_executions != null && (
                  <p className="text-xs text-gray-600 mt-1">
                    {outcome.total_successes}/{outcome.total_executions} success
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===== Helpers ===== */
function StatCard({ label, value, sub, color }) {
  return (
    <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-xl font-bold ${color || 'text-white'}`}>{value}</p>
      <p className="text-[11px] text-gray-600 mt-0.5">{sub}</p>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
